// Re-attach the OOXML-only properties (font size, color, Google-Docs w:shd
// highlight, line spacing, real numbering formats) that mammoth structurally
// cannot emit onto the already-built Plate fragment.
//
// Anchoring: the extractor produced paragraphs in document order; we walk the
// fragment's paragraph-like blocks in the same order and pair them by
// normalized text with a forward-scanning cursor (so an occasional
// mammoth-only or OOXML-only paragraph self-heals instead of shifting
// everything after it). Table CELLS use the same text-cursor match (`cells`)
// as a fallback, but try POSITIONAL (row, cell) index attach first (`tableCells`
// — see attachCellBackgroundsByPosition) whenever a table's OOXML row/cell
// shape matches its converted Plate table 1:1: text-cursor matching alone
// cannot disambiguate a table full of repeated values ("Yes"/"N/A"/"-").
//
// SAFETY: this pass only fills a prop that is absent, and never writes
// listStart directly — the load-time stampOrderedListStarts owns that. It is
// ADDITIVE for every prop except one narrow, deliberate exception: a paragraph
// the source .docx itself declares to be numbered (via direct w:numPr OR a
// paragraph STYLE's w:numPr — mammoth never resolves the latter) gains a list
// marker even when mammoth left the block a plain node, PROVIDED the block has
// non-empty text (§2.2 B2 — a blank style-numbered paragraph must never become
// a numbered blank line). An already-list-detected block is always upgraded to
// the real OOXML format, ungated, exactly as before.
// Run→leaf marks are applied by character-offset alignment and skipped entirely
// when the leaf/run text lengths disagree, so a mismatch can never corrupt text.
//
// Word's real running ordinal for each ordered list item is never written onto
// a node — it rides on this pass's RETURN VALUE (`listOrdinals`), consumed by
// stampWordListRestarts below. There is no transient node prop to leak if a
// later step throws (§2.1 Option A3, revision S3).
import { normalizeText } from './extract';
import { computeOrderedStartFromSiblings } from '../../components/BlockList/BlockList';
import { fitColSizesFromTwips } from '../tableColSizes';
import { fontNameToStack } from '../../fontStacks';
import type { ApplyOoxmlResult, OoxmlCell, OoxmlParagraph, OoxmlRun, OoxmlTableCells, OoxmlTableGrid } from './types';

const PARAGRAPH_BLOCK_TYPES = new Set(['p', 'h1', 'h2', 'h3', 'blockquote']);
const CELL_TYPES = new Set(['td', 'th', 'table_cell', 'table_cell_header']);
const TABLE_TYPES = new Set(['table', 'tableWithoutHeaders']);
const WHITESPACE = /\s/;

function isParagraphBlock(node: any): boolean {
    return node != null && typeof node.type === 'string' && PARAGRAPH_BLOCK_TYPES.has(node.type) && Array.isArray(node.children);
}

function isCell(node: any): boolean {
    return node != null && typeof node.type === 'string' && CELL_TYPES.has(node.type) && Array.isArray(node.children);
}

function isTable(node: any): boolean {
    return node != null && typeof node.type === 'string' && TABLE_TYPES.has(node.type) && Array.isArray(node.children);
}

// Visual column count, colSpan-AWARE — a merged cell (w:gridSpan, arriving as
// element.colSpan / the legacy attributes.colspan) occupies multiple visual
// columns, so counting DOM cells alone undercounts a table where every row
// has a merge. Mirrors pasteNormalize.ts's tableFragmentColumnCount; computed
// inline here rather than imported, since importing it would invert this
// module's layering (ooxml is a lower-level primitive than pasteNormalize).
function visualColumnCount(table: any): number {
    let max = 0;
    for (const row of table.children ?? []) {
        if (row?.type !== 'tr') continue;
        const cols = (row.children ?? [])
            .filter((c: any) => c?.type === 'td' || c?.type === 'th')
            .reduce((n: number, c: any) => n + (Number(c?.colSpan ?? c?.attributes?.colspan) || 1), 0);
        if (cols > max) max = cols;
    }
    return max;
}

function collectLeaves(node: any, out: any[]): void {
    if (node == null) return;
    if (typeof node.text === 'string') {
        out.push(node);
        return;
    }
    if (Array.isArray(node.children)) {
        for (const child of node.children) collectLeaves(child, out);
    }
}

function blockText(node: any): string {
    const leaves: any[] = [];
    collectLeaves(node, leaves);
    return normalizeText(leaves.map((leaf) => leaf.text).join(''));
}

function applyRunToLeaf(leaf: any, run: OoxmlRun): void {
    if (run.fontSizePx && leaf.fontSize == null) leaf.fontSize = run.fontSizePx;
    if (run.color && leaf.color == null) leaf.color = run.color;
    if (run.backgroundColor && leaf.backgroundColor == null) leaf.backgroundColor = run.backgroundColor;
    if (run.underline && leaf.underline == null) leaf.underline = true;
    if (run.bold && leaf.bold == null) leaf.bold = true;
    if (run.italic && leaf.italic == null) leaf.italic = true;
    if (run.strikethrough && leaf.strikethrough == null) leaf.strikethrough = true;
    // The OOXML run carries a BARE face name (`Calibri`); expand it to a full CSS
    // stack so the text renders with metric-close fallbacks on machines without
    // the exact face (macOS has no Calibri) and the FontFamilyMenu can match it.
    if (run.fontFamily && leaf.fontFamily == null) leaf.fontFamily = fontNameToStack(run.fontFamily) ?? run.fontFamily;
}

// When the run stream and the leaf stream cover a different number of visible
// characters (mammoth's HTML text vs the raw-XML run text diverge on fields,
// footnote refs, smart-quote normalization, …), per-character alignment can't
// run. Rather than drop EVERY mark for the paragraph — the old behavior, which
// silently lost Calibri/size on any such paragraph — apply any mark that is
// UNIFORM across all runs. With no per-run variation there is no ambiguity about
// which run a leaf belongs to, so whole-paragraph "Calibri 10pt" formatting
// still lands on every leaf; a paragraph with mixed per-run formatting keeps the
// old conservative skip (no guessing).
function uniformRunValue<T>(runs: OoxmlRun[], pick: (run: OoxmlRun) => T | undefined): T | undefined {
    let value: T | undefined;
    let seen = false;
    for (const run of runs) {
        const current = pick(run);
        if (current == null) return undefined; // a run without the mark → not uniform
        if (!seen) {
            value = current;
            seen = true;
        } else if (current !== value) {
            return undefined;
        }
    }
    return seen ? value : undefined;
}

function applyUniformRunMarks(leaves: any[], runs: OoxmlRun[]): void {
    const fontFamily = uniformRunValue(runs, (run) => run.fontFamily);
    const fontSize = uniformRunValue(runs, (run) => run.fontSizePx);
    const color = uniformRunValue(runs, (run) => run.color);
    const backgroundColor = uniformRunValue(runs, (run) => run.backgroundColor);
    const underline = runs.length > 0 && runs.every((run) => run.underline) ? true : undefined;
    const bold = runs.length > 0 && runs.every((run) => run.bold) ? true : undefined;
    const italic = runs.length > 0 && runs.every((run) => run.italic) ? true : undefined;
    const strikethrough = runs.length > 0 && runs.every((run) => run.strikethrough) ? true : undefined;
    for (const leaf of leaves) {
        if (fontSize && leaf.fontSize == null) leaf.fontSize = fontSize;
        if (color && leaf.color == null) leaf.color = color;
        if (backgroundColor && leaf.backgroundColor == null) leaf.backgroundColor = backgroundColor;
        if (underline && leaf.underline == null) leaf.underline = true;
        if (fontFamily && leaf.fontFamily == null) leaf.fontFamily = fontNameToStack(fontFamily) ?? fontFamily;
        if (bold && leaf.bold == null) leaf.bold = true;
        if (italic && leaf.italic == null) leaf.italic = true;
        if (strikethrough && leaf.strikethrough == null) leaf.strikethrough = true;
    }
}

// Attribute run marks to leaves by matching NON-WHITESPACE characters between
// the run stream and the leaf stream. Whitespace is exactly where upstream
// collapsing (collapseWhiteSpace) makes the two diverge, so ignoring it keeps
// the mapping robust without the brittle full-length equality this used to
// require (which silently dropped every mark — colors, w:shd highlights — on any
// paragraph whose whitespace had been collapsed). A leaf whose non-ws chars all
// fall in one run inherits that run's marks; a leaf spanning runs (mixed
// formatting) is left alone.
function applyRunsToLeaves(block: any, runs: OoxmlRun[]): void {
    if (runs.length === 0) return;
    const leaves: any[] = [];
    collectLeaves(block, leaves);
    if (leaves.length === 0) return;

    const runIndexByNonWs: number[] = [];
    runs.forEach((run, index) => {
        for (const ch of run.text) if (!WHITESPACE.test(ch)) runIndexByNonWs.push(index);
    });
    if (runIndexByNonWs.length === 0) return;

    // Both streams must cover the same visible text. If their non-whitespace
    // counts differ — e.g. the run stream carries more characters than the leaves
    // do — they didn't correspond, so don't guess marks off a mismatched
    // alignment. (Whitespace is excluded, so this stays robust to the upstream
    // whitespace collapsing described above.)
    let leafNonWs = 0;
    for (const leaf of leaves) {
        for (const ch of leaf.text as string) if (!WHITESPACE.test(ch)) leafNonWs += 1;
    }
    if (leafNonWs !== runIndexByNonWs.length) {
        // Counts diverged. Fall back to whole-paragraph uniform marks instead of
        // dropping all fidelity — but ONLY when the leaves don't cover MORE
        // visible text than the runs. If they do, some leaf text has no backing
        // run (its formatting is unknown), so blanket-applying a uniform mark
        // would mislabel it; stay conservative and skip, as before.
        if (leafNonWs <= runIndexByNonWs.length) applyUniformRunMarks(leaves, runs);
        return;
    }

    let cursor = 0;
    for (const leaf of leaves) {
        let firstRun = -1;
        let lastRun = -1;
        let exhausted = false;
        for (const ch of leaf.text as string) {
            if (WHITESPACE.test(ch)) continue;
            if (cursor >= runIndexByNonWs.length) {
                // Leaf has more visible text than the run stream — the two didn't
                // correspond; don't guess a mark for the uncovered tail.
                exhausted = true;
                break;
            }
            const runIndex = runIndexByNonWs[cursor];
            cursor += 1;
            if (firstRun === -1) firstRun = runIndex;
            lastRun = runIndex;
        }
        if (!exhausted && firstRun !== -1 && firstRun === lastRun) applyRunToLeaf(leaf, runs[firstRun]);
    }
}

// Attach cell shadings and row heights by (row, cell)/(row) INDEX rather than
// text search — the only way to disambiguate a table full of repeated values
// ("Yes"/"N/A"/"-"), which the text-cursor match in the main `visit` loop
// below cannot: it can only find "the next occurrence of this text", not "the
// occurrence in this position". All-or-nothing: only applies when the OOXML
// table's row count AND every row's cell count match the converted Plate
// table's exactly — any divergence (mammoth dropped/merged a cell for this
// table) aborts without touching a single cell or row, leaving the
// text-cursor match as the sole (existing) fallback for cell shadings.
function attachCellBackgroundsByPosition(table: any, ooxmlRows: OoxmlCell[][], rowHeights: Array<number | undefined> = []): void {
    const plateRows = (Array.isArray(table.children) ? table.children : []).filter((r: any) => r?.type === 'tr');
    if (plateRows.length !== ooxmlRows.length) return;
    const plateCellRows = plateRows.map((row: any) => (Array.isArray(row.children) ? row.children : []).filter(isCell));
    if (plateCellRows.some((cells: any[], r: number) => cells.length !== ooxmlRows[r].length)) return;

    plateCellRows.forEach((cells: any[], r: number) => {
        cells.forEach((cell: any, c: number) => {
            const { background } = ooxmlRows[r][c];
            // Same additive guard as the text-cursor path: only fill when the
            // cell has no fill of its own yet.
            if (background && cell.background == null && cell.backgroundColor == null) {
                cell.background = background;
            }
        });
    });

    // Twips → px (÷15 = ×96/1440), same conversion as spacingBefore/After.
    plateRows.forEach((row: any, r: number) => {
        const height = rowHeights[r];
        if (height != null && row.size == null) row.size = Math.round(height / 15);
    });
}

function applyToBlock(node: any, paragraph: OoxmlParagraph, ordinals: Array<[any, number]>): void {
    if (paragraph.align && node.align == null) node.align = paragraph.align;
    if (paragraph.lineHeight != null && node.lineHeight == null) node.lineHeight = paragraph.lineHeight;
    if (paragraph.backgroundColor && node.backgroundColor == null) node.backgroundColor = paragraph.backgroundColor;
    // Twips → px (÷15 = ×96/1440).
    if (paragraph.spacingBefore != null && node.spacingBefore == null) node.spacingBefore = Math.round(paragraph.spacingBefore / 15);
    if (paragraph.spacingAfter != null && node.spacingAfter == null) node.spacingAfter = Math.round(paragraph.spacingAfter / 15);

    if (paragraph.list) {
        // UPGRADE: mammoth already detected this block as a list — replace its
        // marker/indent with the real OOXML format. Deliberately UNGATED:
        // mammoth never emits a list item for a blank numbered paragraph, so
        // this branch never has to worry about the blank-paragraph case below.
        const isUpgrade = node.listStyleType != null;
        // CREATE: the source .docx says this paragraph is numbered (commonly
        // via a paragraph STYLE's w:numPr, which mammoth never resolves — see
        // resolveParagraphProps), but mammoth left the block a plain node.
        // MANDATORY gate on non-empty block text (§2.2 B2 / S6): a blank
        // style-numbered paragraph must not become a numbered blank line — the
        // Infotel CAM document alone has 248 of them.
        const isCreate = !isUpgrade && blockText(node) !== '';
        if (isUpgrade || isCreate) {
            node.listStyleType = paragraph.list.listStyleType;
            if (node.indent == null) node.indent = paragraph.list.indent;
            if (paragraph.list.ordinal != null) ordinals.push([node, paragraph.list.ordinal]);
        }
    } else if (paragraph.indentLeft != null && node.indent == null) {
        // 360 twips = 0.25in ≈ one 24px IndentPlugin step. Net against hanging/
        // firstLine before stepping: Word renders `left=720 hanging=720` at NET
        // margin 0 (the hanging pulls the first/only line back). This does not
        // render a first-line indent as its own Plate prop — it only corrects
        // the net-left math.
        const effectiveLeft = Math.max(0, paragraph.indentLeft - (paragraph.indentHanging ?? 0) + (paragraph.indentFirstLine ?? 0));
        const steps = Math.round(effectiveLeft / 360);
        if (steps > 0) node.indent = steps;
    }

    applyRunsToLeaves(node, paragraph.runs);
}

export function applyOoxmlProps(
    fragment: any[],
    paragraphs: OoxmlParagraph[],
    cells: OoxmlCell[] = [],
    grids: OoxmlTableGrid[] = [],
    tableCells: OoxmlTableCells[] = [],
): ApplyOoxmlResult {
    let cursor = 0;
    let matched = 0;
    let skipped = 0;
    let cellCursor = 0;
    let cellsMatched = 0;
    let gridCursor = 0;
    let tableCellsCursor = 0;
    const listOrdinals: Array<[any, number]> = [];

    const findParagraph = (text: string): number => {
        if (text === '') return -1;
        for (let i = cursor; i < paragraphs.length; i += 1) {
            if (paragraphs[i].anchorText === text) return i;
        }
        return -1;
    };

    const findCell = (text: string): number => {
        if (text === '') return -1;
        for (let i = cellCursor; i < cells.length; i += 1) {
            if (cells[i].anchorText === text) return i;
        }
        return -1;
    };

    // Unlike the other three finders, '' IS allowed to match here: a blank
    // table can have a real grid entry (extractTableGrids keys purely on a
    // usable width source, not on text), and `===` keeps a blank anchor from
    // ever matching a non-blank one. Residual risk: two blank tables where an
    // earlier one has NO entry can shift a later one's entry onto the wrong
    // table — the same duplicate-text ambiguity already accepted for
    // non-empty text (module doc's "table full of repeated values"), no worse
    // than today's total loss.
    const findGrid = (text: string): number => {
        for (let i = gridCursor; i < grids.length; i += 1) {
            if (grids[i].anchorText === text) return i;
        }
        return -1;
    };

    const findTableCells = (text: string): number => {
        if (text === '') return -1;
        for (let i = tableCellsCursor; i < tableCells.length; i += 1) {
            if (tableCells[i].anchorText === text) return i;
        }
        return -1;
    };

    const visit = (nodes: any[]): void => {
        for (const node of nodes) {
            if (isCell(node)) {
                const index = findCell(blockText(node));
                if (index >= 0) {
                    const { background } = cells[index];
                    // The cell renderer reads `background ?? backgroundColor`; only
                    // fill when the cell has no fill of its own yet.
                    if (background && node.background == null && node.backgroundColor == null) {
                        node.background = background;
                    }
                    cellCursor = index + 1;
                    cellsMatched += 1;
                }
                // Recurse for the cell's own paragraph blocks.
                if (Array.isArray(node.children)) visit(node.children);
            } else if (isParagraphBlock(node)) {
                const text = blockText(node);
                const index = findParagraph(text);
                if (index >= 0) {
                    applyToBlock(node, paragraphs[index], listOrdinals);
                    cursor = index + 1;
                    matched += 1;
                } else if (text !== '') {
                    skipped += 1;
                }
            } else if (isTable(node)) {
                // Shared by both lookups below so a table's (potentially large)
                // subtree text is only walked once per table, not once per lookup.
                const tableAnchor = grids.length || tableCells.length ? blockText(node) : '';
                if (node.colSizes == null && grids.length) {
                    const gi = findGrid(tableAnchor);
                    if (gi >= 0) {
                        gridCursor = gi + 1;
                        let widths = grids[gi].widths;
                        const cols = visualColumnCount(node);
                        if (widths.length > cols) widths = widths.slice(0, cols); // > → truncate
                        if (widths.length === cols && cols > 0) {
                            // == → attach; < (unhandled above) → skip, leave unset.
                            node.colSizes = fitColSizesFromTwips(widths);
                        }
                    }
                }
                if (tableCells.length) {
                    const ti = findTableCells(tableAnchor);
                    if (ti >= 0) {
                        tableCellsCursor = ti + 1;
                        // Best-effort positional attach; a shape mismatch leaves every
                        // cell/row untouched here and the per-cell text-cursor match
                        // below (unconditional recursion) is still the fallback for
                        // cell shadings (row height has no such fallback).
                        attachCellBackgroundsByPosition(node, tableCells[ti].rows, tableCells[ti].rowHeights);
                    }
                }
                // Recurse regardless of the match outcome so cell shadings and
                // paragraph fidelity still apply inside this table's rows/cells.
                if (Array.isArray(node.children)) visit(node.children);
            } else if (Array.isArray(node?.children)) {
                visit(node.children);
            }
        }
    };

    visit(fragment);
    return { fragment, stats: { matched, skipped, cellsMatched }, listOrdinals };
}

/**
 * Consume the ordinals collected by applyOoxmlProps and stamp plate's native
 * `listRestart` (@platejs/utils KEYS.listRestart) ONLY where Word's real
 * running ordinal diverges from the positional derivation the renderer / the
 * load-time stamper / plateToHtml would all compute anyway — i.e. exactly the
 * points where an interruption (table, style change, a second numId, …) made
 * Word's own count differ from a fresh recount (§2.1 Option A3).
 *
 * Mutates the fragment nodes in place, mirroring applyToBlock. Nothing to
 * clean up on a throw: no transient prop is ever written (see types.ts).
 */
export function stampWordListRestarts(fragment: any[], listOrdinals: Array<[any, number]>): void {
    if (listOrdinals.length === 0) return;
    const ordinalByNode = new Map<any, number>(listOrdinals);

    const visit = (nodes: any[]): void => {
        nodes.forEach((node, index) => {
            const ordinal = ordinalByNode.get(node);
            if (ordinal != null && node?.listStyleType != null) {
                const positional = computeOrderedStartFromSiblings(nodes, index, node.listStyleType, node.indent);
                if (positional !== ordinal) node.listRestart = ordinal;
            }
            if (Array.isArray(node?.children)) visit(node.children);
        });
    };

    visit(fragment);
}
